/**
 * V2 API implementation of CustomerLiveEventRepository.
 *
 * Endpoints:
 * - GET customer/live-events/upcoming?page=&perPage=
 * - GET customer/live-events/past?page=&perPage=
 * - GET customer/live-events/:eventId/join
 *
 * Pure TypeScript - no React imports.
 */

import {httpClientV2} from "../../../../infrastructure/http";
import type {
  CustomerLiveEventRepository,
  CustomerLiveEventPaginationParams,
  CustomerLiveEventJoinParams,
  PaginatedCustomerLiveEventBookings,
} from "../../domain/types";
import type {JoinLiveEventResult} from "../../../live-events/domain/types";
import {CustomerLiveEventMapperV2} from "../mappers/mapper.v2";
import type {
  CustomerLiveEventListV2Response,
  CustomerJoinLiveEventV2Response,
} from "../mappers/mapper.types";

export class CustomerLiveEventRepositoryV2 implements CustomerLiveEventRepository {
  async getUpcoming(
    params?: CustomerLiveEventPaginationParams
  ): Promise<PaginatedCustomerLiveEventBookings> {
    const endpoint = this.buildListEndpoint("upcoming", params);
    const response = await httpClientV2.get<CustomerLiveEventListV2Response>(endpoint);
    return CustomerLiveEventMapperV2.toBookingList(response.data);
  }

  async getPast(
    params?: CustomerLiveEventPaginationParams
  ): Promise<PaginatedCustomerLiveEventBookings> {
    const endpoint = this.buildListEndpoint("past", params);
    const response = await httpClientV2.get<CustomerLiveEventListV2Response>(endpoint);
    return CustomerLiveEventMapperV2.toBookingList(response.data);
  }

  async join(params: CustomerLiveEventJoinParams): Promise<JoinLiveEventResult> {
    // v2 joins by eventId
    const response = await httpClientV2.get<CustomerJoinLiveEventV2Response>(
      `customer/live-events/${params.eventId}/join`
    );
    return CustomerLiveEventMapperV2.joinToDomain(response.data);
  }

  private buildListEndpoint(
    filter: "upcoming" | "past",
    params?: CustomerLiveEventPaginationParams
  ): string {
    // v2 uses camelCase perPage
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) {
      queryParams.append("page", params.page.toString());
    }
    if (params?.perPage !== undefined) {
      queryParams.append("perPage", params.perPage.toString());
    }
    const queryString = queryParams.toString();
    return queryString
      ? `customer/live-events/${filter}?${queryString}`
      : `customer/live-events/${filter}`;
  }
}
